import { useState } from "react";
import { AlertTriangle, RefreshCw, Loader2 } from "lucide-react";

/**
 * What the whole app falls back to when something throws.
 *
 * This replaced a bare <PageLoader /> — a spinner. That made every crash
 * indistinguishable from a slow load: on 31 Aug 2026 a colleague sat on an
 * endless spinner across all three surfaces and had no way to tell the app had
 * broken, and neither did we until the errors were dug out of Sentry by hand.
 * A crash should say so and offer a way out.
 *
 * "Reset the app" is here because the failure mode we actually see is a stale
 * cached copy: the service worker keeps the app usable on poor stadium signal,
 * but a fan or an admin can end up running a build weeks old against a schema
 * that has moved on. Clearing the worker, its caches and the persisted query
 * cache is the fix, and it should not require someone talking them through
 * browser dev tools at a match.
 */
interface AppErrorFallbackProps {
  error?: unknown;
  eventId?: string;
  resetError?: () => void;
}

export const AppErrorFallback = ({ error, eventId, resetError }: AppErrorFallbackProps) => {
  const [resetting, setResetting] = useState(false);

  const hardReset = async () => {
    setResetting(true);
    try {
      // Order matters: drop the workers first so a reload cannot be served
      // from a cache we are about to delete.
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      localStorage.removeItem("IDSEAT_QUERY_CACHE");
    } catch {
      // Best effort — a blocked storage API must not stop the reload below,
      // which is still the most likely thing to get the person moving again.
    }
    window.location.reload();
  };

  const detail = error instanceof Error ? error.message : null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-sm text-center">
        <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mx-auto mb-5">
          <AlertTriangle className="w-7 h-7 text-muted-foreground" />
        </div>

        <h1 className="text-xl font-semibold text-foreground mb-2">Something went wrong</h1>
        <p className="text-sm text-muted-foreground leading-relaxed mb-6">
          This page couldn&rsquo;t load. Reloading usually fixes it.
        </p>

        <div className="space-y-2">
          <button
            onClick={() => (resetError ? resetError() : window.location.reload())}
            className="w-full h-11 rounded-lg bg-foreground text-background text-sm font-medium inline-flex items-center justify-center gap-2 transition-opacity hover:opacity-90"
          >
            <RefreshCw className="w-4 h-4" />
            Reload the page
          </button>

          <button
            onClick={hardReset}
            disabled={resetting}
            className="w-full h-11 rounded-lg border border-border text-sm text-muted-foreground inline-flex items-center justify-center gap-2 transition-colors hover:bg-muted disabled:opacity-60"
          >
            {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {resetting ? "Resetting…" : "Still stuck? Reset the app"}
          </button>
        </div>

        {detail ? (
          <p className="mt-6 text-xs text-muted-foreground/80 break-words font-mono">{detail}</p>
        ) : null}

        {/* Support can paste this straight into Sentry instead of guessing from timestamps. */}
        {eventId ? (
          <p className="mt-3 text-[11px] text-muted-foreground/70 font-mono">
            Reference: {eventId}
          </p>
        ) : null}
      </div>
    </div>
  );
};
